import React, { useState } from 'react';
import toast from 'react-hot-toast';
import './ContactUs.css';
import API_BASE_URL from '../../config';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaWhatsapp, FaFacebookMessenger } from 'react-icons/fa';
import { HiMail, HiPhone, HiLocationMarker, HiClock } from 'react-icons/hi';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    orderNumber: '',
    department: 'general',
    message: '',
    agreeToTerms: false,
    subscribeNewsletter: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const departments = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'support', label: 'Customer Support' },
    { value: 'orders', label: 'Order Issues' },
    { value: 'returns', label: 'Returns & Refunds' },
    { value: 'wholesale', label: 'Wholesale/Bulk Orders' },
    { value: 'partnership', label: 'Partnership Opportunities' },
    { value: 'feedback', label: 'Feedback & Suggestions' },
    { value: 'technical', label: 'Technical Support' }
  ];

  const subjects = [
    'Product Information',
    'Order Status',
    'Shipping & Delivery',
    'Return Request',
    'Size Guide Help',
    'Payment Issues',
    'Website Feedback',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (formData.phone && !/^[0-9+\-\s()]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.subject) newErrors.subject = 'Please select a subject';
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    if (formData.message.trim().length < 10) newErrors.message = 'Message must be at least 10 characters';
    if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill all required fields correctly');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // In a real app, you would send this data to your backend
      // const response = await fetch(`${API_BASE_URL}/api/contact`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(formData)
      // });
      
      toast.success('Your message has been sent successfully! We will get back to you within 24 hours.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        orderNumber: '',
        department: 'general',
        message: '',
        agreeToTerms: false,
        subscribeNewsletter: false
      });
      setErrors({});
      
    } catch (error) {
      toast.error('Failed to send message. Please try again later.');
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = {
    phone: '+977-1-4444444',
    whatsapp: '+977-9801234567',
    email: 'support@neplishop.com',
    address: 'Kamalpokhari, Kathmandu, Nepal',
    hours: 'Sunday-Friday: 9:00 AM - 6:00 PM NPT',
    emergency: 'For urgent order issues: +977-1-4444445'
  };

  const faqs = [
    {
      question: 'How long does shipping take?',
      answer: 'Within Kathmandu Valley: 1-2 business days. Outside valley: 3-7 business days. International: 7-14 business days.'
    },
    {
      question: 'What is your return policy?',
      answer: 'We offer 7-day returns for defective items. Items must be unused with original tags. Please contact us within 7 days of delivery.'
    },
    {
      question: 'Do you ship internationally?',
      answer: 'Yes! We ship to over 50 countries worldwide. Shipping costs and times vary by destination.'
    },
    {
      question: 'How can I track my order?',
      answer: 'You will receive a tracking number via email once your order ships. You can also check order status in your account dashboard.'
    }
  ];

  return (
    <div className="contact-us-page">
      {/* Hero Section */}
      <div className="contact-hero">
        <div className="hero-content">
          <h1>Get in Touch with NepliShop</h1>
          <p>We're here to help! Contact us for any questions about our products, orders, or services.</p>
          <div className="hero-stats">
            <div className="stat-item">
              <span className="stat-number">24/7</span>
              <span className="stat-label">Email Support</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">2h</span>
              <span className="stat-label">Avg. Response Time</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">98%</span>
              <span className="stat-label">Satisfaction Rate</span>
            </div>
          </div>
        </div>
      </div>

      <div className="contact-container">
        <div className="contact-content">
          {/* Contact Information Section */}
          <div className="contact-info-section">
            <h2>Contact Information</h2>
            <p className="section-subtitle">Choose your preferred way to reach us</p>
            
            <div className="contact-methods-grid">
              <div className="contact-method">
                <div className="method-icon">
                  <FaPhone />
                </div>
                <h3>Call Us</h3>
                <p className="method-detail">{contactInfo.phone}</p>
                <p className="method-note">Available Sunday-Friday, 9 AM - 6 PM NPT</p>
                <a href={`tel:${contactInfo.phone.replace(/\D/g, '')}`} className="method-action">
                  Call Now
                </a>
              </div>

              <div className="contact-method">
                <div className="method-icon">
                  <FaWhatsapp />
                </div>
                <h3>WhatsApp</h3>
                <p className="method-detail">{contactInfo.whatsapp}</p>
                <p className="method-note">24/7 support for quick queries</p>
                <a href={`https://wa.me/${contactInfo.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="method-action">
                  Message on WhatsApp
                </a>
              </div>

              <div className="contact-method">
                <div className="method-icon">
                  <FaEnvelope />
                </div>
                <h3>Email Us</h3>
                <p className="method-detail">{contactInfo.email}</p>
                <p className="method-note">We reply within 24 hours</p>
                <a href={`mailto:${contactInfo.email}`} className="method-action">
                  Send Email
                </a>
              </div>

              <div className="contact-method">
                <div className="method-icon">
                  <FaMapMarkerAlt />
                </div>
                <h3>Visit Us</h3>
                <p className="method-detail">{contactInfo.address}</p>
                <p className="method-note">By appointment only</p>
                <a href="https://maps.google.com/?q=Kamalpokhari,Kathmandu,Nepal" target="_blank" rel="noopener noreferrer" className="method-action">
                  Get Directions
                </a>
              </div>
            </div>

            <div className="emergency-contact">
              <h4>🚨 Emergency Contact</h4>
              <p>For urgent order issues: <strong>{contactInfo.emergency}</strong></p>
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="contact-form-section">
            <h2>Send Us a Message</h2>
            <p className="section-subtitle">Fill out the form below and we'll get back to you as soon as possible</p>
            
            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="name">
                    Full Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className={errors.name ? 'error' : ''}
                    disabled={isSubmitting}
                  />
                  {errors.name && <span className="error-message">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email">
                    Email Address <span className="required">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="your.email@example.com"
                    className={errors.email ? 'error' : ''}
                    disabled={isSubmitting}
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number (Optional)</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+977 98XXXXXXXX"
                    className={errors.phone ? 'error' : ''}
                    disabled={isSubmitting}
                  />
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="orderNumber">Order Number (Optional)</label>
                  <input
                    type="text"
                    id="orderNumber"
                    name="orderNumber"
                    value={formData.orderNumber}
                    onChange={handleChange}
                    placeholder="#ORD123456"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="department">
                    Department <span className="required">*</span>
                  </label>
                  <select
                    id="department"
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className={errors.department ? 'error' : ''}
                    disabled={isSubmitting}
                  >
                    {departments.map(dept => (
                      <option key={dept.value} value={dept.value}>
                        {dept.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">
                    Subject <span className="required">*</span>
                  </label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={errors.subject ? 'error' : ''}
                    disabled={isSubmitting}
                  >
                    <option value="">Select a subject</option>
                    {subjects.map(subject => (
                      <option key={subject} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                  {errors.subject && <span className="error-message">{errors.subject}</span>}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="message">
                  Your Message <span className="required">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Please describe your inquiry in detail..."
                  rows="6"
                  className={errors.message ? 'error' : ''}
                  disabled={isSubmitting}
                />
                <div className="character-count">
                  {formData.message.length}/2000 characters
                </div>
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              <div className="form-checkboxes">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className={errors.agreeToTerms ? 'error' : ''}
                  />
                  <label htmlFor="agreeToTerms">
                    I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> <span className="required">*</span>
                  </label>
                </div>
                {errors.agreeToTerms && <span className="error-message">{errors.agreeToTerms}</span>}

                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    id="subscribeNewsletter"
                    name="subscribeNewsletter"
                    checked={formData.subscribeNewsletter}
                    onChange={handleChange}
                    disabled={isSubmitting}
                  />
                  <label htmlFor="subscribeNewsletter">
                    Subscribe to our newsletter for updates and promotions
                  </label>
                </div>
              </div>

              <button 
                type="submit" 
                className="submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner"></span>
                    Sending...
                  </>
                ) : (
                  'Send Message'
                )}
              </button>

              <p className="form-note">
                <strong>Note:</strong> All fields marked with <span className="required">*</span> are required.
                We typically respond within 2 hours during business hours.
              </p>
            </form>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            {faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
          <div className="faq-link">
            <a href="/faq" className="view-all-faq">
              View All FAQ →
            </a>
          </div>
        </div>

        {/* Business Hours Section */}
        <div className="business-hours">
          <h2>Business Hours</h2>
          <div className="hours-grid">
            <div className="hours-item">
              <HiClock className="hours-icon" />
              <div>
                <h3>Customer Support</h3>
                <p>Sunday-Friday: 9:00 AM - 6:00 PM NPT</p>
                <p>Saturday: 10:00 AM - 4:00 PM NPT</p>
              </div>
            </div>
            <div className="hours-item">
              <HiPhone className="hours-icon" />
              <div>
                <h3>Phone Support</h3>
                <p>Sunday-Friday: 9:00 AM - 6:00 PM NPT</p>
                <p>Emergency: 24/7 (for urgent order issues)</p>
              </div>
            </div>
            <div className="hours-item">
              <HiMail className="hours-icon" />
              <div>
                <h3>Email Support</h3>
                <p>24/7 - We reply within 24 hours</p>
                <p>Priority: support@neplishop.com</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;

